import React, { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'expo-router';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { MaterialCommunityIcons as Icon } from '@expo/vector-icons';
import { REDESIGN_COLORS } from '../../styles/colors';
import { useAuth } from '../../context/AuthContext';
import { useTier } from '../../context/TierContext';
import { prefetchBillingSummary } from '../../hooks/useBillingSummary';
import { useBillingDeeplink } from '../../hooks/useBillingDeeplink';
import { readBillingSnapshot, clearBillingSnapshot, diffSnapshot } from '../../utils/billingSnapshot';
import { calendarDaysUntil } from '../../utils/calendarDays';
import { trackBillingAction } from '../../utils/billingAnalytics';
import { usePostHog } from 'posthog-js/react';
import BillingModal from '../billing/BillingModal';

const MS_PER_DAY = 1000 * 60 * 60 * 24;

const formatDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Calendar-day diff (local midnight boundary) so the counter drops by
// exactly 1 each calendar day instead of locking on the trial-start
// hour-of-day for ~24 hours. See utils/calendarDays.js for the rationale.
const daysUntil = (value) => calendarDaysUntil(value);

// Derives a user-friendly plan label from tier + subscription state.
// Real plan-price wiring is D-035 territory; this uses what's available today.
// D-014: status alone signals FR, `founders_rate_coupon` no longer consulted.
// 2026-05-26 pivot: tier='Free' with a post-trial / post-grandfather / lapsed
// subscription_status shows as 'Locked'. Brand-new pre-trial users (status
// 'none') keep the 'Free' label so they read as a positive starting point.
// trial_active / grandfather_active grant Pro access but carry tier='Free' in
// the DB (access is gated on subscription_status, not the tier column), so they
// are resolved before the tier==='Free' fallthrough to read as 'Pro'. The
// Status row below still differentiates Trial vs Grandfather window.
const LOCKED_STATUSES = new Set([
  'trial_expired',
  'grandfather_expired',
  'cancelled',
  'past_due',
  'paused',
]);
const resolvePlanLabel = ({ tier, subscriptionStatus }) => {
  // Step 8: a carded user (card-on-file, pre day-7 charge) reads as Pro. Access
  // is gated on subscription_status, not the tier column (which stays Free until
  // the day-7 charge flips it), so resolve it before the tier==='Free' fallthrough.
  if (
    subscriptionStatus === 'trial_active' ||
    subscriptionStatus === 'trial_carded' ||
    subscriptionStatus === 'grandfather_active'
  ) {
    return 'Pro';
  }
  if (tier === 'Free' && LOCKED_STATUSES.has(subscriptionStatus)) return 'Locked';
  if (tier === 'Free' || subscriptionStatus === 'none') return 'Free';
  if (tier === 'Premium') return 'Premium';
  return 'Pro';
};

// Status pill content (plain words, not raw enum values).
const resolveStatusBadge = ({ subscriptionStatus, proExpiresAt }) => {
  const inGrace = proExpiresAt && new Date(proExpiresAt).getTime() > Date.now();
  switch (subscriptionStatus) {
    case 'active':
      return { label: 'Active', tone: 'good' };
    case 'trial_active':
      return { label: 'Trial', tone: 'info' };
    case 'trial_carded':
      // Step 8: card on file, full Pro, first charge deferred to day 7.
      return { label: 'Trial, card on file', tone: 'info' };
    case 'grandfather_active':
      return { label: 'Grandfather window', tone: 'info' };
    case 'past_due':
      return inGrace
        ? { label: 'Past due, action needed', tone: 'warn' }
        : { label: 'Past due', tone: 'bad' };
    case 'cancelled':
      return inGrace
        ? { label: 'Cancels at period end', tone: 'warn' }
        : { label: 'Cancelled', tone: 'muted' };
    case 'paused':
      return inGrace ? { label: 'Paused', tone: 'warn' } : { label: 'Paused', tone: 'muted' };
    case 'trial_expired':
      return { label: 'Trial ended', tone: 'muted' };
    case 'grandfather_expired':
      return { label: 'Grandfather window ended', tone: 'muted' };
    case 'none':
    default:
      return { label: 'Free', tone: 'muted' };
  }
};

// "Next" event date row: renewal, trial-end, grace-end, founder's-rate-end, or none.
const resolveNextEvent = ({
  subscriptionStatus,
  subscriptionId,
  proExpiresAt,
  trialStartedAt,
  trialCohortDays,
  grandfatherExpiresAt,
}) => {
  if (subscriptionStatus === 'trial_active' && trialStartedAt && trialCohortDays) {
    const end = new Date(new Date(trialStartedAt).getTime() + trialCohortDays * MS_PER_DAY);
    return { label: 'Trial ends', date: end };
  }
  // Step 8: a carded user pays at the original day-7 mark (trial_started + cohort).
  // The authoritative date is read from Paddle in get-billing-summary / BillingModal;
  // this account-screen line uses the local clock as the same fallback.
  if (subscriptionStatus === 'trial_carded' && trialStartedAt && trialCohortDays) {
    const firstCharge = new Date(new Date(trialStartedAt).getTime() + trialCohortDays * MS_PER_DAY);
    return { label: 'First charge on', date: firstCharge };
  }
  if (subscriptionStatus === 'grandfather_active' && grandfatherExpiresAt) {
    return { label: "Founder's Rate window closes", date: new Date(grandfatherExpiresAt) };
  }
  if (subscriptionStatus === 'active' && proExpiresAt) {
    // CBP is also subscription_status='active' but has no subscription_id —
    // it's a one-time 90-day pass that expires, doesn't renew. Recurring subs
    // (subscription_id != null) actually renew on proExpiresAt.
    if (!subscriptionId) {
      return { label: 'Expires on', date: new Date(proExpiresAt) };
    }
    return { label: 'Renews on', date: new Date(proExpiresAt) };
  }
  if (subscriptionStatus === 'past_due' && proExpiresAt) {
    return { label: 'Grace period ends', date: new Date(proExpiresAt) };
  }
  if (subscriptionStatus === 'cancelled' && proExpiresAt) {
    return { label: 'Access ends', date: new Date(proExpiresAt) };
  }
  if (subscriptionStatus === 'paused' && proExpiresAt) {
    return { label: 'Access ends', date: new Date(proExpiresAt) };
  }
  return null;
};

// True if we should offer a Manage billing button (user has an existing Paddle relationship).
const hasPaddleBillingRelationship = (subscriptionStatus) =>
  // Step 8: trial_carded has a real Paddle sub + customer, so it gets the
  // Manage subscription affordance (switch / cancel / update payment method).
  ['active', 'past_due', 'cancelled', 'paused', 'trial_active', 'trial_carded', 'grandfather_active'].includes(
    subscriptionStatus,
  );

const MembershipSection = () => {
  const {
    tier,
    subscriptionStatus,
    subscriptionId,
    proExpiresAt,
    trialStartedAt,
    trialCohortDays,
    grandfatherExpiresAt,
    triggerUpgradeModal,
  } = useTier();

  const planLabel = resolvePlanLabel({ tier, subscriptionStatus });
  const statusBadge = resolveStatusBadge({ subscriptionStatus, proExpiresAt });
  const nextEvent = resolveNextEvent({
    subscriptionStatus,
    subscriptionId,
    proExpiresAt,
    trialStartedAt,
    trialCohortDays,
    grandfatherExpiresAt,
  });
  const router = useRouter();
  const { user } = useAuth() ?? {};
  const posthog = usePostHog();
  const [billingModalOpen, setBillingModalOpen] = useState(false);
  // Tracks the action set by an in-section Resume / Reactivate click (as
  // opposed to deeplink auto-open). Lets BillingModal open in the correct
  // sub-state for paused / cancelled-in-grace recovery without needing to
  // round-trip through the URL.
  const [manualInitialAction, setManualInitialAction] = useState(null);
  const [manualSource, setManualSource] = useState(null);
  const { shouldAutoOpen, flagEnabled, source: deeplinkSource, action: deeplinkAction, clearDeeplink } = useBillingDeeplink();

  // CBP users have status='active' but no recurring sub — nothing to manage in
  // BillingModal (no plan to change, no cancel-recurring, no payment method on
  // file). Pass expires naturally on `proExpiresAt` and reverts to Free.
  const isCBP = subscriptionStatus === 'active' && !subscriptionId;
  const showManageBilling = hasPaddleBillingRelationship(subscriptionStatus) && !isCBP;
  // Step 8: a carded user already has full Pro + a recurring sub funding it, so
  // hide the upgrade CTA (Manage subscription is the only relevant action). This
  // mirrors the 'active' suppression.
  const showUpgradeCTA = !['active', 'trial_carded'].includes(subscriptionStatus);
  const trialDaysLeft = subscriptionStatus === 'trial_active'
    ? daysUntil(nextEvent?.date)
    : null;

  // Warm the billing-summary cache on mount so the modal opens instantly.
  useEffect(() => {
    if (!user?.id || !flagEnabled || !showManageBilling) return;
    prefetchBillingSummary(user.id);
  }, [user?.id, flagEnabled, showManageBilling]);

  // Auto-open when email/portal deeplink is present.
  useEffect(() => {
    if (shouldAutoOpen && user?.id && flagEnabled) setBillingModalOpen(true);
  }, [shouldAutoOpen, user?.id, flagEnabled]);

  // Paddle-portal-return diff (task 7.15).
  useEffect(() => {
    if (!user?.id) return;
    if (deeplinkSource !== 'paddle_portal_return' || deeplinkAction !== 'refresh') return;
    const snap = readBillingSnapshot();
    if (!snap) return;
    prefetchBillingSummary(user.id).then((fresh) => {
      if (!fresh) return;
      const changed = diffSnapshot(snap, fresh);
      const evt = changed.length ? 'billing_portal_return_changed' : 'billing_portal_return_unchanged';
      trackBillingAction(posthog, evt, { changed_fields: changed });
      clearBillingSnapshot();
    });
  }, [user?.id, deeplinkSource, deeplinkAction, posthog]);

  const handleManageBilling = useCallback(() => {
    setManualInitialAction(null);
    setManualSource(null);
    setBillingModalOpen(true);
  }, []);

  const handleBillingModalClose = useCallback(() => {
    setBillingModalOpen(false);
    setManualInitialAction(null);
    setManualSource(null);
    if (shouldAutoOpen) clearDeeplink();
  }, [shouldAutoOpen, clearDeeplink]);

  // Paused or cancelled-in-grace: open BillingModal in resume / reactivate
  // mode so billing-action runs the Paddle recovery flow against the
  // existing subscription. For other states (free, expired, grace-exhausted
  // cancelled, past_due), fall back to triggerUpgradeModal which starts a
  // fresh Paddle checkout. Mirrors the resolveHeaderCTA logic in HeaderCTA.
  const handleUpgrade = useCallback(() => {
    const inGrace = proExpiresAt && new Date(proExpiresAt).getTime() > Date.now();
    if (subscriptionStatus === 'paused') {
      setManualInitialAction('resume');
      setManualSource('membership_resume');
      setBillingModalOpen(true);
      return;
    }
    if (subscriptionStatus === 'cancelled' && inGrace) {
      setManualInitialAction('reactivate');
      setManualSource('membership_reactivate');
      setBillingModalOpen(true);
      return;
    }
    triggerUpgradeModal({ source: 'free_header_cta' });
  }, [subscriptionStatus, proExpiresAt, triggerUpgradeModal]);

  return (
    <View style={styles.card}>
      <View style={styles.cardHeader}>
        <Icon name="crown-outline" size={24} color={REDESIGN_COLORS.ctaBlue} />
        <Text style={styles.cardTitle}>Membership</Text>
      </View>

      <View style={styles.cardContent}>
        <View style={styles.infoRow}>
          <Text style={styles.label}>Plan</Text>
          <Text style={styles.value}>{planLabel}</Text>
        </View>
        <View style={styles.divider} />

        <View style={styles.infoRow}>
          <Text style={styles.label}>Status</Text>
          <View style={[styles.badge, styles[`badge_${statusBadge.tone}`]]}>
            <Text style={[styles.badgeText, styles[`badgeText_${statusBadge.tone}`]]}>
              {statusBadge.label}
            </Text>
          </View>
        </View>

        {trialDaysLeft !== null && (
          <>
            <View style={styles.divider} />
            <View style={styles.infoRow}>
              <Text style={styles.label}>Trial days remaining</Text>
              <Text style={styles.value}>
                {trialDaysLeft === 0 ? 'Ends today' : `${trialDaysLeft} ${trialDaysLeft === 1 ? 'day' : 'days'}`}
              </Text>
            </View>
          </>
        )}

        {nextEvent && (
          <>
            <View style={styles.divider} />
            <View style={styles.infoRow}>
              <Text style={styles.label}>{nextEvent.label}</Text>
              <Text style={styles.value}>{formatDate(nextEvent.date)}</Text>
            </View>
          </>
        )}
      </View>

      {(showManageBilling || showUpgradeCTA) && (
        <View style={styles.actions}>
          {showUpgradeCTA && (
            <Pressable
              style={[styles.actionBtn, styles.actionBtnPrimary]}
              onPress={handleUpgrade}
              accessibilityRole="button"
              accessibilityLabel="Upgrade"
            >
              <Text style={styles.actionBtnPrimaryText}>
                {subscriptionStatus === 'paused' ? 'Resume' :
                  (subscriptionStatus === 'cancelled' || subscriptionStatus === 'past_due') ? 'Reactivate' :
                  'Upgrade'}
              </Text>
            </Pressable>
          )}
          {showManageBilling && (
            <Pressable
              style={[styles.actionBtn, styles.actionBtnSecondary]}
              onPress={handleManageBilling}
              accessibilityRole="button"
              accessibilityLabel="Manage subscription"
            >
              <Text style={styles.actionBtnSecondaryText}>Manage subscription</Text>
            </Pressable>
          )}
        </View>
      )}

      {flagEnabled && (
        <BillingModal
          isOpen={billingModalOpen}
          onClose={handleBillingModalClose}
          userId={user?.id}
          source={manualSource ?? deeplinkSource ?? 'settings'}
          initialAction={manualInitialAction ?? (shouldAutoOpen ? deeplinkAction : null)}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.06)',
    padding: 20,
    marginBottom: 16,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
  },
  cardTitle: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 16,
    color: REDESIGN_COLORS.textPrimary,
  },
  cardContent: {
    // Empty but kept for parity with ProfileScreen .cardContent wrapper
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 10,
    gap: 16,
  },
  label: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 13,
    color: REDESIGN_COLORS.textSecondary,
  },
  value: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 13,
    color: REDESIGN_COLORS.textPrimary,
    textAlign: 'right',
    flexShrink: 1,
  },
  divider: {
    height: 1,
    backgroundColor: 'rgba(255,255,255,0.05)',
  },
  badge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    borderWidth: 1,
  },
  badgeText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 11,
    letterSpacing: 0.3,
  },
  badge_good: {
    backgroundColor: 'rgba(58,232,105,0.12)',
    borderColor: 'rgba(58,232,105,0.4)',
  },
  badgeText_good: { color: '#3AE869' },
  badge_info: {
    backgroundColor: 'rgba(5,86,247,0.12)',
    borderColor: 'rgba(5,86,247,0.4)',
  },
  badgeText_info: { color: '#6FA0FF' },
  badge_warn: {
    backgroundColor: 'rgba(255,123,0,0.12)',
    borderColor: 'rgba(255,123,0,0.4)',
  },
  badgeText_warn: { color: '#FFB073' },
  badge_bad: {
    backgroundColor: 'rgba(255,0,0,0.12)',
    borderColor: 'rgba(255,0,0,0.4)',
  },
  badgeText_bad: { color: '#FF6B6B' },
  badge_muted: {
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderColor: 'rgba(255,255,255,0.12)',
  },
  badgeText_muted: { color: 'rgba(255,255,255,0.6)' },
  actions: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 14,
  },
  actionBtn: {
    flex: 1,
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionBtnPrimary: {
    backgroundColor: REDESIGN_COLORS.ctaBlue,
  },
  actionBtnPrimaryText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 13,
    color: '#FFFFFF',
  },
  actionBtnSecondary: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.2)',
  },
  actionBtnSecondaryText: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 13,
    color: REDESIGN_COLORS.textPrimary,
  },
  cancelLink: {
    marginTop: 18,
    alignItems: 'center',
  },
  cancelLinkText: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 12,
    color: 'rgba(255,255,255,0.45)',
    textDecorationLine: 'underline',
  },
});

export default MembershipSection;
